import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { scrollToId } from "../lib/scroll";

const LINKS = [
  { label: "L'approche", hash: "#approche", testId: "nav-link-approche" },
  { label: "Dirigeants & QVT", hash: "#dirigeants", testId: "nav-link-pme" },
  { label: "Individuel", hash: "#individuel", testId: "nav-link-individuel" },
  { label: "À propos", hash: "#apropos", testId: "nav-link-apropos" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (hash) => {
    setOpen(false);
    scrollToId(hash);
  };

  return (
    <motion.header
      initial={{ y: -70, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.4 }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled
          ? "bg-[#0A1120]/85 backdrop-blur-xl border-b border-[#D4AF37]/15"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10 h-[70px] flex items-center justify-between">
        <button
          data-testid="nav-brand-logo"
          onClick={() => window.__lenis?.scrollTo(0, { duration: 1.4 })}
          className="flex items-center gap-3 group"
        >
          <img
            src="/assets/signature.png"
            alt="Monogramme FV"
            className="h-9 w-9 object-contain transition-transform duration-500 group-hover:scale-110"
          />
          <span className="font-serif-display text-lg tracking-wide text-white">
            Frédérique <span className="italic text-gold-gradient">Vasseur</span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.hash}
              data-testid={l.testId}
              onClick={() => go(l.hash)}
              className="text-sm text-slate-300 hover:text-[#F3E5AB] transition-colors duration-300 tracking-wide"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-cta-booking-button"
            onClick={() => go("#contact")}
            className="ml-2 rounded-full border border-[#D4AF37]/50 px-5 py-2 text-sm font-medium text-[#F3E5AB] transition-all duration-300 hover:bg-[#D4AF37] hover:text-[#0A1120] hover:border-[#D4AF37]"
          >
            Prendre rendez-vous
          </button>
        </nav>

        <button
          data-testid="nav-mobile-menu-button"
          className="lg:hidden text-slate-200"
          onClick={() => setOpen((v) => !v)}
          aria-label="Menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: "easeInOut" }}
            className="lg:hidden overflow-hidden bg-[#0A1120]/95 backdrop-blur-xl border-b border-[#D4AF37]/15"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <button
                  key={l.hash}
                  data-testid={`${l.testId}-mobile`}
                  onClick={() => go(l.hash)}
                  className="text-left text-base text-slate-200 hover:text-[#F3E5AB]"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="nav-cta-booking-button-mobile"
                onClick={() => go("#contact")}
                className="mt-2 rounded-full bg-[#D4AF37] px-5 py-3 text-sm font-semibold text-[#0A1120]"
              >
                Prendre rendez-vous
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
